import React, { useEffect, useState, useCallback } from 'react';
import { Row, Col, Layout, Card } from 'antd';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import moment from 'moment';
import FeedsComponent from '../Organism/FeedsComponent';
import BirthdayWidget from '../Atoms/BirthdayWidget'
import WorkAnniversaryWidget from '../Atoms/WorkAnniversaryWidget';
import AppHeaderHomePageTemplate from '../Organism/AppHeaderHomePageTemplate';
import axios from 'axios';
import './Feeds.css';

const { Content } = Layout;

interface BirthdayPerson {
  userId: string;
  displayName: string;
  countryName: string;
}

interface AnniversaryPerson {
  userId: string;
  displayName: string;
  countryName: string;
}

const ApteanFeedsPage: React.FC = () => {
  const [userProfileImages, setUserProfileImages] = useState<{ [key: string]: string | null }>({});
  const [nominatorName, setNominatorName] = useState('');
  const [nominatorEmail, setNominatorEmail] = useState('');
  const [birthdays, setBirthdays] = useState<BirthdayPerson[]>([]);
  const [anniversary, setAnniversary] = useState<AnniversaryPerson[]>([]);
  const [loadingBirthdays, setLoadingBirthdays] = useState(false);
  const [loadingAnniversary, setLoadingAnniversary] = useState(false);
  const [cachedToken, setCachedToken] = useState<string | null>(null);
  const [tokenExpiry, setTokenExpiry] = useState<number>(0);
  const { instance, accounts } = useMsal();
  
  const callMsGraph = async (token: any) => {
    const headers = new Headers();
    headers.append("Authorization", `Bearer ${token}`);
    const response = await fetch(`https://graph.microsoft.com/v1.0/me?`, {
      method: "GET",
      headers,
    }).then((response) => response.json());
    return response;
  };

  // Optimized token fetching with caching
  const fetchToken = async () => {
    const now = Date.now();
    // Return cached token if it's still valid (expires in 5 minutes)
    if (cachedToken && tokenExpiry > now) {
      return cachedToken;
    }
    
    const res = await instance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0]
    });
    
    // Cache token for 5 minutes (300000 ms)
    setCachedToken(res.idToken);
    setTokenExpiry(now + 300000);
    return res.idToken;
  };
    
  useEffect(() => {
    fetchToken();
  }, []);

  // Use ref to track if profile data has been fetched
  const profileDataFetched = React.useRef(false);

  const requestProfileData = useCallback(async () => {
    // Prevent multiple calls if already fetched
    if (profileDataFetched.current) {
      return;
    }

    try {
      const response = await instance.acquireTokenSilent({
        scopes: ["User.Read", "User.Read.All"],
        authority: `https://login.microsoftonline.com/560ec2b0-df0c-4e8c-9848-a15718863bb6`,
      });
      const data = await callMsGraph(response.accessToken);
      localStorage.setItem("UserMail", data?.mail.toLowerCase());
      setNominatorName(data?.displayName || 'Default Nominator');
      setNominatorEmail(data?.mail.toLowerCase());
      profileDataFetched.current = true;
    } catch (error) {
      console.error("Silent token acquisition failed, acquiring token using redirect");
      instance.loginRedirect(loginRequest);
    }
  }, [instance]);

  // Fetch data in parallel for better performance
  const fetchWidgetData = async () => {
    try {
      await Promise.all([
        fetchBirthdays(),
        fetchAnniversary()
      ]);
    } catch (error) {
      console.error('Error fetching widget data:', error);
    }
  };

  useEffect(() => {
    requestProfileData();
    fetchWidgetData();
  }, [requestProfileData]);

  const fetchBirthdays = async () => {
    try {
      setLoadingBirthdays(true);
      const token = await fetchToken();
      // Mock data for birthday widget based on the sample provided
      // In a real scenario, you would fetch this from an API
      const endpoint = await axios.get(`${process.env.REACT_APP_API_URL}/api/Feeds/BirthdayData`,{
        headers: {
          Authorization: `Bearer ${token}`,
        }
      }); 
      if (endpoint.data && endpoint.data.length > 0) 
        {
          setBirthdays(endpoint.data);
          // Fetch profile images for birthday people
          const userIds = endpoint.data.map((person: BirthdayPerson) => person.userId);
          fetchUserProfileImages(userIds);
      } else {
        setBirthdays([]);
      }
    } catch (error) {
      console.error('Error fetching birthdays:', error);
      setBirthdays([]);
    } finally {
      setLoadingBirthdays(false);
    }
  };

  const fetchAnniversary = async () => {
    try {
      setLoadingAnniversary(true);
      const token = await fetchToken();
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/Feeds/WorkAnniversaryData`,{
        headers: {
          Authorization: `Bearer ${token}`,
        }
      }); 
      if (response.data && response.data.length > 0) 
        {
          setAnniversary(response.data);
          // Fetch profile images for anniversary people
          const userIds = response.data.map((person: AnniversaryPerson) => person.userId);
          fetchUserProfileImages(userIds);
      } else {
        setAnniversary([]);
      }
    } catch (error) {
      console.error('Error fetching anniversaries:', error);
      setAnniversary([]);
    } finally {
      setLoadingAnniversary(false);
    }
  };
  
  const fetchUserProfileImages = async (userIds: string[]): Promise<void> => {
    try {
      // Filter out already cached user IDs to avoid redundant API calls
      const uncachedUserIds = userIds.filter(id => !userProfileImages[id]);
      
      if (uncachedUserIds.length === 0) {
        return; // All images already cached
      }

      const token = await fetchToken();
      const params = new URLSearchParams();
      uncachedUserIds.forEach(id => params.append('userIds', id));
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/Users/GetProfileImages?${params.toString()}`,{headers: {Authorization: `Bearer ${
        token}`}});
      
      if (!response.ok) {
        throw new Error(`Error fetching user profile images: ${response.statusText}`);
      }

      const data: { [key: string]: string | null } = await response.json();
      // Merge new images with existing ones
      setUserProfileImages(prev => ({ ...prev, ...data }));
    } catch (error) {
      console.error('Error fetching user profile images:', error);
    }
  };

  const currentMonth = moment().format('MMMM');

  return (
    <Layout style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <AppHeaderHomePageTemplate/>
      <Content className="content">
        <Row gutter={24}>
          <Col xs={24} sm={24} md={16} lg={16} style={{ width: '62rem' }}>
            <FeedsComponent />
          </Col>
          <Col xs={24} sm={24} md={8} lg={8}>
            {loadingBirthdays ? (
              <Card 
                title="Loading Birthdays..." 
                style={{
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  borderRadius: '8px',
                  marginTop: '20px'
                }}
                loading={true}
              />
            ) : (
              <BirthdayWidget 
                currentMonth={currentMonth} 
                birthdays={birthdays}
                userProfileImages={userProfileImages}
              />
            )}
            {loadingAnniversary ? (
              <Card 
                title="Loading Work Anniversaries..." 
                style={{
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  borderRadius: '8px',
                  marginTop: '20px'
                }}
                loading={true}
              />
            ) : (
              <WorkAnniversaryWidget 
                currentMonth={currentMonth} 
                anniversarys={anniversary}
                userProfileImages={userProfileImages}
              />
            )}
          </Col>
        </Row>
      </Content>
    </Layout>
  );
};

export default ApteanFeedsPage;